require('./RAInvestorProfile.scss');

// @vendors
const React = require('react');
const PropTypes = require('prop-types');
const classNames = require('classnames');

// @commons
const { OKLink } = require('commonsComponents/OKLink');
const {
    DATE_PICKER_CUSTOM_DATE_FORMAT,
} = require('constants/commonsComponents');

// @components
const FormattedTextWithComponents = require('components/broker/brokerFormattedTextWithComponents/brokerFormattedTextWithComponents');

// @utilities
const { FormattedText, formatText } = require('core/i18n').i18n;
const {
    formatPercentageWithDecimals,
    formatWithoutRoundedPercentage,
} = require('../../utilities/RACurrencyHelper');
const RAAssetsHelper = require('../../utilities/RAAssetsHelper');
const { Portfolios } = require('../../utilities/RAPortfolioHelper');
const DateHelper = require('utilities/dateHelper');

// @constants
const { PERCENTAGE_SYMBOL } = require('constants/index');
const {
    ACTIVE_CLASS_MONEY_MARKET_ID,
    ACTIVE_CLASS_FIXED_INCOME_ID,
    ACTIVE_CLASS_EQUITY_ID,
    ACTIVE_CLASS_REAL_ASSETS_ID,
} = require('../../raConstants/index');

class RAInvestorProfile extends React.Component {
    getItemValueContainerClass(assetClassId) {
        const customClass = classNames(
            'ra-investor-profile__item-value-container',
            {
                ['ra-investor-profile__item-value-container--money-market']:
                    assetClassId === ACTIVE_CLASS_MONEY_MARKET_ID,
                ['ra-investor-profile__item-value-container--fixed-income']:
                    assetClassId === ACTIVE_CLASS_FIXED_INCOME_ID,
                ['ra-investor-profile__item-value-container--equity']:
                    assetClassId === ACTIVE_CLASS_EQUITY_ID,
                ['ra-investor-profile__item-value-container--real-assets']:
                    assetClassId === ACTIVE_CLASS_REAL_ASSETS_ID,
            },
        );

        return customClass;
    }

    buildDistributionAssetsTabs(immAssetAllocations) {
        const assetsTabsBlock = immAssetAllocations.map(assetAllocation => {
            const assetClassId = assetAllocation.get('assetClassId');
            const assetItemPercentContainerClassName = this.getItemValueContainerClass(
                assetClassId,
            );

            return (
                <div
                    key={assetClassId}
                    className="ra-investor-profile__tab-item"
                >
                    <div className={assetItemPercentContainerClassName}>
                        <span className="ra-investor-profile__item-label">
                            {assetAllocation.get('assetClass')}
                        </span>
                    </div>
                    <div className="ra-investor-profile__item-wrapper">
                        <span className="ra-investor-profile__item-value">
                            {formatPercentageWithDecimals(
                                assetAllocation.get('weight'),
                                false,
                            )}
                        </span>
                        <span className="ra-investor-profile__item-symbol">
                            {PERCENTAGE_SYMBOL}
                        </span>
                    </div>
                </div>
            );
        });

        return assetsTabsBlock;
    }

    buildDistributionBar(immAssetAllocations) {
        const bar = immAssetAllocations.map(assetAllocation => {
            const assetClassId = assetAllocation.get('assetClassId');
            const barPercentage = formatWithoutRoundedPercentage(
                assetAllocation.get('weight'),
            );
            const barColor = RAAssetsHelper.getBarColor(assetClassId);
            const style = {
                backgroundColor: barColor,
                width: barPercentage,
                height: '5px',
                display: 'inline-block',
            };

            return <span key={assetClassId} style={style} />;
        });

        return (
            <div className="ra-investor-profile__distribution-bar">{bar}</div>
        );
    }

    getObjectiveImageClass(iconType) {
        const customClass = classNames(
            'ra-investor-profile__objective-image',
            `ra-investor-profile__objective-image--${iconType}`,
        );

        return customClass;
    }

    buildObjectiveContent() {
        const { immSimulation } = this.props;

        const objectiveName = immSimulation.get('name');
        const endDate =
            immSimulation.get('endDate') &&
            DateHelper.formatDate(
                immSimulation.get('endDate'),
                formatText(DATE_PICKER_CUSTOM_DATE_FORMAT),
            );
        const imageClasses = this.getObjectiveImageClass(
            immSimulation.get('type'),
        );

        return (
            <div className="row ra-investor-profile__objective">
                <div className="col-md-6 ra-investor-profile__objective-image-wrapper">
                    <div className={imageClasses}></div>
                    <div className="ra-investor-profile__objective-title">
                        <span>{objectiveName}</span>
                    </div>
                </div>
                <div className="col-md-6 ra-investor-profile__objective-date">
                    <div className="ra-investor-profile__objective-date-value">
                        <i className="ra-investor-profile__objective-icon icon-skyline"></i>
                        <span>{endDate}</span>
                    </div>
                    <div className="ra-investor-profile__objective-date-label">
                        <FormattedText value="raInvestorProfile-objectiveDateDescription" />
                    </div>
                </div>
            </div>
        );
    }

    buildInvestmentPlan(hasObjective) {
        let investmentPlan;

        if (hasObjective) {
            investmentPlan = (
                <FormattedText
                    value="raInvestorProfile-objectiveInvestmentPlan"
                    className="ra-investor-profile__tabs-title"
                />
            );
        } else {
            const { selectedPortfolioId } = this.props;
            const portfolio = Portfolios[selectedPortfolioId];

            investmentPlan = (
                <FormattedText
                    value="raInvestorProfile-nonObjectiveInvestmentPlan"
                    className="ra-investor-profile__tabs-title"
                    injectedStrings={[portfolio.portfolioName]}
                />
            );
        }

        return investmentPlan;
    }

    render() {
        const {
            immAssetAllocations,
            immResults,
            hasObjective,
            selectedPortfolioId,
            initialContributionAmount,
        } = this.props;
        const investorProfile = immResults.get('clientRiskProfileTitle', '');
        const investorProfileDescription = immResults.get(
            'clientRiskProfileDescription',
            '',
        );

        const titleHighlightBlock = (
            <span className="ra-investor-profile__title ra-investor-profile__title--highlight">
                {investorProfile}
            </span>
        );
        const distributionAssetsTabsBlock = this.buildDistributionAssetsTabs(
            immAssetAllocations,
        );
        const distributionBarBlock = this.buildDistributionBar(
            immAssetAllocations,
        );
        const objectiveContent = hasObjective
            ? this.buildObjectiveContent()
            : null;
        const investmentPlan = this.buildInvestmentPlan(hasObjective);
        const url = `/myprofile/robo/hire/portfolioDistribution/${selectedPortfolioId}/${initialContributionAmount}`;

        return (
            <div className="ra-investor-profile">
                {objectiveContent}
                <div className="row">
                    <div className="col-md-6">
                        <h3 className="ra-investor-profile__title">
                            <FormattedTextWithComponents
                                value="raInvestorProfile-title"
                                injectedComponents={[titleHighlightBlock]}
                            />
                        </h3>
                        <span className="ra-investor-profile__info">
                            {investorProfileDescription}
                        </span>
                    </div>
                    <div className="col-md-6">
                        {investmentPlan}
                        <div className="ra-investor-profile__container">
                            <div className="ra-investor-profile__container-tabs">
                                {distributionAssetsTabsBlock}
                            </div>
                            {distributionBarBlock}
                        </div>
                        <div className="ra-investor-profile__bottom">
                            <OKLink modifier="complementary" to={url}>
                                <FormattedText value="raInvestorProfile-link" />
                            </OKLink>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

RAInvestorProfile.propTypes = {
    immAssetAllocations: PropTypes.object.isRequired,
    immResults: PropTypes.object.isRequired,
    initialContributionAmount: PropTypes.number,
    immSimulation: PropTypes.object,
    selectedPortfolioId: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ]),
    hasObjective: PropTypes.bool.isRequired,
};

RAInvestorProfile.defaultProps = {
    selectedPortfolioId: 1,
};

module.exports = RAInvestorProfile;
